import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import { People, ExpandMore as ExpandMoreIcon } from '@mui/icons-material'
import { Form, Formik, FormikHelpers, getIn } from 'formik'
import { inject, observer } from 'mobx-react'
import * as React from 'react'
import { Helmet } from 'react-helmet'
import { useParams } from 'react-router-dom'
import * as Yup from 'yup'
import PatientActionBar from '../common/ActionBar/PatientActionBar'
import AddressField from '../common/AddressField/AddressField'
import CardWithNoTitle from '../common/CardWithNoTitle/CardWithNoTitle'
import FullWidthField from '../common/FullWidthField'
import IsActiveToggle from '../common/IsActiveToggle'
import NumberMaskedInput from '../common/NumberMaskedInput'
import PhoneNumberMask from '../common/PhoneNumberMask'
import ResponsiveAppBar from '../common/ResponsiveAppBar'
import { noWhitespacePhone, noWhitespaceString } from '../common/YupExtensions'
import { useUnsavedChangesWarning } from '../common/hooks/useUnsavedChangesWarning'
import UnsavedChangesDialog from '../common/UnsavedChangesDialog/UnsavedChangesDialog'
import { DefaultPatient, IEpisodeOfCare, IPatient, IReferral } from '../Definitions'
import { IStores } from '../Stores'
import DeletePatientDialog from './DeletePatientDialog/DeletePatientDialog'
import EpisodesOfCare from './EpisodesOfCare/EpisodesOfCare'
import {
  createIPatientFormValues,
  IPatientFormValues,
  toIPatient,
} from './PatientFormValues'
import Referrals from './PatientReferrals/Referrals'
import PreExistingConditionSelect from './PreExistingConditionSelect/PreExistingConditionSelect'
import moment from 'moment'
import { SwaggerResponse } from 'src/generated_client'
import { DropdownOption } from 'src/viewModels/DropdownOption'

const PatientSchema = Yup.object().shape({
  address: Yup.object({
    city: noWhitespaceString('City is required', true),
    line1: noWhitespaceString('Street address is required', true),
    line2: Yup.string(),
    state: noWhitespaceString('State is required', true),
    zip: noWhitespaceString('Zip code is required', true),
  }),
  allowTexting: Yup.boolean(),
  allowContact: Yup.boolean(),
  cellPhone: noWhitespacePhone(),
  dateOfBirthText: Yup.date().required('Birth date is required'),
  emailAddress: noWhitespaceString().email('Email Address must be a valid email'),
  firstName: noWhitespaceString('First name is required', true),
  gender: Yup.string(),
  heightFeet: Yup.string(),
  heightInches: Yup.string(),
  homePhone: noWhitespacePhone(),
  hxOfSx: Yup.string(),
  isActive: Yup.boolean(),
  lastName: noWhitespaceString('Last name is required', true),
  patientComments: Yup.string(),
  sSN: noWhitespaceString().test('len', 'Must be 4 characters', (val) =>
    val ? val.length === 4 : true
  ),
  weight: Yup.number(),
  workPhone: noWhitespacePhone(),
})

interface IPatientRouteParams {
  patientId?: string
}

interface IUnsavedChangesProps {
  showCancelDialog: boolean
  handleCancelClick: (hasChanges: boolean) => void
  handleCancelConfirm: (onNavigate: () => void) => void
  handleCancelDismiss: () => void
  setIsDirty: (isDirty: boolean) => void
}

interface IPatientProps extends IUnsavedChangesProps {
  routeParams: IPatientRouteParams
  episodeOfCare?: IEpisodeOfCare
  getPatientById?: (id: string) => Promise<void>
  isConfirmDialogOpen?: boolean
  isLoading?: boolean
  openConfirmDialog?: () => void
  patient?: IPatient
  rerouteToPath?: (path: string) => void
  savePatient?: (patient: IPatient) => Promise<SwaggerResponse<void>>
  setPageSize?: (pageSize: number) => void
  setSelectedPatient?: (patient: IPatient | undefined) => void
  setSelectedReferral?: (referral: IReferral | undefined) => void
  closeTab?: (fallbackPath?: string) => void
  setReturn?: (fallbackPath?: string) => void
  patientToQuotes?: boolean
  resetPatient?: () => void
}

interface IPatientState {
  reload: boolean
  allowContact: boolean
  expandedPatientPanel: string
  expandedReferralPanel: string
}

class Patient extends React.Component<IPatientProps, IPatientState> {
  constructor(props: IPatientProps) {
    super(props)
    this.state = {
      expandedPatientPanel: props.patient && props.patient.isNew ? 'patientPanel' : '',
      expandedReferralPanel: props.patient && props.patient.isNew ? '' : 'referralPanel',
      reload: false,
      allowContact:
        props.patient && props.patient.allowContact != undefined
          ? props.patient?.allowContact
          : true,
    }
  }

  public genderOptions = new Array<DropdownOption>()

  private async setup() {
    const { routeParams } = this.props
    this.setState({ reload: false })
    this.setState({
      expandedPatientPanel: routeParams && routeParams.patientId ? '' : 'patientPanel',
      expandedReferralPanel: routeParams && routeParams.patientId ? 'referralPanel' : '',
    })
    if (routeParams && routeParams.patientId) {
      this.props.getPatientById!(routeParams.patientId).then(() => {
        this.setState({
          allowContact:
            this.props.patient && this.props.patient.allowContact != undefined
              ? this.props.patient?.allowContact
              : true,
        })
        this.props.setPageSize!(10)
      })
    } else {
      this.props.setSelectedPatient!(DefaultPatient(true))
    }
  }

  public async componentDidMount() {
    var female = new DropdownOption()
    female.description = 'Female'
    female.id = 'Female'
    var male = new DropdownOption()
    male.description = 'Male'
    male.id = 'Male'
    var unknown = new DropdownOption()
    unknown.description = '[UNKNOWN]'
    unknown.id = ''
    this.genderOptions.push(unknown, male, female)

    await this.setup()
  }

  public async componentDidUpdate(prevProps: IPatientProps) {
    if (this.props.routeParams.patientId !== prevProps.routeParams.patientId) {
      await this.setup()
    }
  }

  public componentWillUnmount() {
    this.props.setSelectedPatient!(undefined)
    this.props.setSelectedReferral!(undefined)
  }

  public handleSave = (submitForm: () => void, reload: boolean) => () => {
    this.setState({ reload }, () => {
      submitForm()
    })
  }

  public save = async (
    values: IPatientFormValues,
    formikBag: FormikHelpers<IPatientFormValues>
  ) => {
    const patient = await toIPatient(values, this.props.patient)

    patient.allowContact = this.state.allowContact
    patient.dateOfBirth = new Date(
      moment(patient.dateOfBirthText, 'YYYY-MM-DD').toISOString()
    )

    this.props.savePatient!(patient)
      .then(() => {
        formikBag.setSubmitting(false)
        if (this.state.reload) {
          this.redirectToEdit(patient.id)
          this.setup()
        } else {
          this.returnToPatients()
        }
      })
      .catch(() => formikBag.setSubmitting(false))
  }

  private returnToPatients = () => {
    ;(window as any)._skipBeforeUnload = true
    if (sessionStorage.getItem('patientTab') === '1') {
      this.props.setReturn!()
    } else if (this.props.patientToQuotes) {
      this.props.setReturn!('/quotes')
      this.props.resetPatient!()
    }
    this.props.closeTab!('/patients')
    setTimeout(() => {
      ;(window as any)._skipBeforeUnload = false
    }, 1000)
  }

  private handleCancelClick = (isDirty: boolean) => {
    if (isDirty) {
      this.props.handleCancelClick(isDirty)
    } else {
      this.returnToPatients()
    }
  }

  private redirectToEdit = (id: string) => {
    this.props.rerouteToPath!(`/patients/patient/${id}`)
    this.setup()
  }

  public getValue(item: DropdownOption) {
    return item.id
  }

  public getName(item: DropdownOption) {
    return item.description
  }

  private handleIsActiveChange =
    (values: IPatientFormValues, setFieldValue: (field: string, value: any) => void) =>
    () => {
      setFieldValue('isActive', !values.isActive)
    }

  private calculateBMI(values: IPatientFormValues) {
    const feet = !!values.heightFeet ? parseInt(values.heightFeet, 10) : 0
    const inches = !!values.heightInches ? parseInt(values.heightInches, 10) : 0
    const weight = !!values.weight ? parseInt(values.weight, 10) : 0
    if (feet > 0 && weight > 0) {
      const weightInKg = weight / 2.2046
      const heightInMeters = (feet * 12 + inches) * 0.0254
      const bmi = (weightInKg / (heightInMeters * heightInMeters)).toFixed(1).toString()
      return bmi
    }
    return 'N/A'
  }

  private handlePatientChange =
    (panel: string) => (_: React.SyntheticEvent, __: boolean) => {
      this.setState({
        expandedPatientPanel: panel === this.state.expandedPatientPanel ? '' : panel,
      })
    }

  private handleReferralChange =
    (panel: string) => (_: React.SyntheticEvent, __: boolean) => {
      this.setState({
        expandedReferralPanel: panel === this.state.expandedReferralPanel ? '' : panel,
      })
    }

  private changeAllowContact = () => {
    this.setState({ allowContact: !this.state.allowContact })
  }

  public renderPatientHTML = (
    patient: any,
    values: any,
    setFieldValue: any,
    hasValue: any
  ) => {
    return (
      <>
        <CardWithNoTitle>
          <Grid container spacing={2} style={{ paddingTop: '10px' }}>
            <Grid item md={3} xs={6}>
              <FullWidthField
                name="firstName"
                label="First Name"
                variant="outlined"
                required
                InputProps={{ style: { height: '40px' } }}
              />
            </Grid>
            <Grid item md={3} xs={6}>
              <FullWidthField
                name="lastName"
                label="Last Name"
                variant="outlined"
                required
                InputProps={{ style: { height: '40px' } }}
              />
            </Grid>
            <Grid item md={3} xs={6}>
              <TextField
                inputProps={{
                  id: 'dateOfBirthText',
                  style: { padding: '8px', height: '24px' },
                }}
                InputLabelProps={{ shrink: true }}
                type="date"
                name="dateOfBirthText"
                variant="outlined"
                className="smallDateTextbox"
                label="Birth Date"
                value={values?.dateOfBirthText}
                onChange={(event) => setFieldValue('dateOfBirthText', event.target.value)}
                fullWidth
                required
              />
            </Grid>
            <Grid item md={3} xs={6}>
              <FullWidthField
                name="sSN"
                label="Last 4 of SSN"
                variant="outlined"
                InputProps={{ style: { height: '40px' } }}
              />
            </Grid>
          </Grid>
          <div style={{ width: '100vw' }}>
            <Accordion
              style={{ marginTop: '10px' }}
              expanded={this.state.expandedPatientPanel === 'patientPanel'}
              onChange={this.handlePatientChange('patientPanel')}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography color="textSecondary" variant="body2">
                  CONTACT INFORMATION AND DEMOGRAPHICS
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container item xs={12} spacing={1}>
                  <Grid container item md={6} xs={12}>
                    <Grid container item spacing={2}>
                      <Grid
                        container
                        item
                        lg={7}
                        md={7}
                        xs={12}
                        style={{ paddingBottom: 28, paddingTop: 10 }}
                      >
                        <AddressField
                          name={'address'}
                          label="ADDRESS"
                          showPhone={false}
                          outlined={true}
                          required={true}
                        />
                        <Grid item xs={12}>
                          <FullWidthField
                            name="emailAddress"
                            label="Email Address"
                            variant="outlined"
                            InputProps={{ style: { height: '40px' } }}
                          />
                        </Grid>
                      </Grid>
                      <Grid
                        item
                        container
                        lg={5}
                        md={5}
                        xs={12}
                        spacing={1}
                        style={{
                          paddingLeft: '20px',
                          paddingTop: '25px',
                          paddingBottom: '30px',
                          paddingRight: '0px',
                        }}
                      >
                        <Grid item xs={12}>
                          <FullWidthField
                            name="cellPhone"
                            label="Cell #"
                            variant="outlined"
                            InputProps={{
                              inputComponent: PhoneNumberMask,
                              style: { height: '40px' },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FullWidthField
                            name="homePhone"
                            label="Home #"
                            variant="outlined"
                            InputProps={{
                              inputComponent: PhoneNumberMask,
                              style: { height: '40px' },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FullWidthField
                            name="workPhone"
                            label="Work #"
                            variant="outlined"
                            InputProps={{
                              inputComponent: PhoneNumberMask,
                              style: { height: '40px' },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormControlLabel
                            label="Allow Texting"
                            labelPlacement="end"
                            control={
                              <Checkbox
                                checked={values.allowTexting}
                                onChange={(e) =>
                                  setFieldValue('allowTexting', e.target.checked)
                                }
                                color="secondary"
                                inputProps={{
                                  id: 'allow-texting-filter',
                                }}
                                value="allowTexting"
                                name="allowTexting"
                              />
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormControlLabel
                            label="Allow Contact"
                            labelPlacement="end"
                            control={
                              <Checkbox
                                checked={this.state.allowContact}
                                onChange={this.changeAllowContact}
                                color="secondary"
                                inputProps={{
                                  id: 'allow-contact-filter',
                                }}
                                value="allowContact"
                                name="allowContact"
                              />
                            }
                          />
                        </Grid>
                      </Grid>
                      <Grid item xs={12} style={{ paddingLeft: '16px' }}>
                        <FullWidthField
                          name="patientComments"
                          label="Patient Comments"
                          variant="outlined"
                          fullWidth
                          InputProps={{ style: { height: '40px' } }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <Grid
                      container
                      direction="row"
                      spacing={2}
                      style={{ paddingTop: 10 }}
                    >
                      <Grid item xs={3}>
                        <Autocomplete
                          size="small"
                          id="gender"
                          options={this.genderOptions}
                          getOptionLabel={(option: any) => option.description}
                          value={
                            this.genderOptions.find((opt) => opt.id === values.gender) ||
                            null
                          }
                          onChange={(_: any, value: any) =>
                            setFieldValue('gender', value ? value.id : '')
                          }
                          renderInput={(params: any) => (
                            <TextField
                              {...params}
                              label="Gender"
                              variant="outlined"
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                            />
                          )}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <FullWidthField
                          name="heightFeet"
                          label="Height-ft"
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            inputComponent: NumberMaskedInput,
                            inputProps: {
                              guide: false,
                              numberOfDigits: 1,
                            },
                            style: { height: '40px' },
                          }}
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <FullWidthField
                          name="heightInches"
                          label="Height-in"
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            inputComponent: NumberMaskedInput,
                            inputProps: {
                              guide: false,
                              numberOfDigits: 2,
                            },
                            style: { height: '40px' },
                          }}
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <FullWidthField
                          name="weight"
                          label="Weight"
                          variant="outlined"
                          InputLabelProps={{
                            shrink: hasValue('weight', values),
                          }}
                          InputProps={{
                            inputComponent: NumberMaskedInput,
                            inputProps: {
                              guide: false,
                              numberOfDigits: 3,
                              style: { padding: '8px' },
                            },
                            style: { height: '40px' },
                          }}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          label="BMI"
                          InputLabelProps={{
                            shrink: true,
                          }}
                          variant="outlined"
                          disabled
                          value={this.calculateBMI(values)}
                          InputProps={{ style: { height: '40px' } }}
                        />
                      </Grid>
                    </Grid>
                    <Grid item container direction="row">
                      <Grid item xs={12}>
                        <PreExistingConditionSelect
                          patientId={patient.id}
                          patientPreExistingConditions={values.preExistingConditions}
                          setFieldValue={setFieldValue}
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      container
                      direction="row"
                      spacing={2}
                      style={{ paddingTop: 10 }}
                    >
                      <Grid item xs={12}>
                        <FullWidthField
                          name="hxOfSx"
                          label="Hx of Sx"
                          variant="outlined"
                          InputProps={{ style: { height: '40px' } }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item md={12}>
                    <IsActiveToggle
                      isActive={values.isActive}
                      onChange={this.handleIsActiveChange(values, setFieldValue)}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
            {patient && !patient.isNew ? (
              <Accordion
                expanded={this.state.expandedReferralPanel === 'referralPanel'}
                onChange={this.handleReferralChange('referralPanel')}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography color="textSecondary" variant="body2">
                    REFERRALS AND EPISODES OF CARE
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container item xs={12} spacing={1}>
                    <Grid item xs={12}>
                      <Referrals />
                    </Grid>
                    <Grid item xs={12}>
                      <EpisodesOfCare />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ) : (
              <></>
            )}
          </div>
        </CardWithNoTitle>
      </>
    )
  }

  public renderReferralsAndEOC = () => {
    return (
      <>
        <Grid container>
          <Grid item md={6} xs={12}>
            <Paper style={{ borderRadius: '5px', marginTop: '4px', paddingTop: '16px' }}>
              <Referrals />
            </Paper>
          </Grid>
          <Grid item md={6} xs={12}>
            <Paper style={{ borderRadius: '5px', marginTop: '4px', paddingTop: '16px' }}>
              <EpisodesOfCare />
            </Paper>
          </Grid>
        </Grid>
        `
      </>
    )
  }

  public render() {
    const { patient } = this.props
    const hasValue = (field: string, values: IPatientFormValues) => {
      const value = getIn(values, field)

      return !!value
    }
    const deletePatientDialog = this.props.isConfirmDialogOpen && (
      <DeletePatientDialog open={true} returnToPatients={this.returnToPatients} />
    )

    const title = (patientInfo: IPatient, includeDetails: boolean) => {
      var result = patientInfo && patientInfo.isNew ? 'Add New Patient' : 'Edit Patient'

      if (includeDetails) {
        result += `: ${this.props.patient?.lastName}, ${
          this.props.patient?.firstName
        } (${moment(this.props.patient?.dateOfBirthText).format('MM/DD/YYYY')})`
      }
      return result
    }

    return (
      <>
        {patient ? (
          <>
            <Helmet>
              <title>{title(patient, true)}</title>
            </Helmet>
            <Formik
              initialValues={createIPatientFormValues(patient)}
              onSubmit={this.save}
              validationSchema={PatientSchema}
              enableReinitialize={true}
            >
              {({ dirty, setFieldValue, values, submitForm, isValid, isSubmitting }) => {
                // Update isDirty through props
                React.useEffect(() => {
                  this.props.setIsDirty(dirty)
                }, [dirty])

                return (
                  <Form>
                    <ResponsiveAppBar title={title(patient, false)} pageIcon={<People />}>
                      <Grid
                        style={{ paddingLeft: '8px' }}
                        container
                        direction="row"
                        spacing={1}
                        justifyContent="flex-end"
                      >
                        <Grid item style={{ paddingLeft: '8px' }}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => this.handleCancelClick(dirty)}
                            style={{ backgroundColor: '#e0e0e0', color: '#000' }}
                          >
                            Cancel
                          </Button>
                        </Grid>
                        <Grid item>
                          <Button
                            size="small"
                            color="secondary"
                            variant="contained"
                            disabled={
                              !isValid ||
                              isSubmitting ||
                              (!dirty &&
                                this.state.allowContact ==
                                  this.props.patient?.allowContact)
                            }
                            onClick={this.handleSave(submitForm, true)}
                          >
                            Save
                          </Button>
                        </Grid>
                        <Grid item>
                          <Button
                            size="small"
                            color="secondary"
                            variant="contained"
                            disabled={
                              !isValid ||
                              isSubmitting ||
                              (!dirty &&
                                this.state.allowContact ==
                                  this.props.patient?.allowContact)
                            }
                            onClick={this.handleSave(submitForm, false)}
                          >
                            Save and Close
                          </Button>
                        </Grid>
                      </Grid>
                    </ResponsiveAppBar>
                    {patient && !patient.isNew && <PatientActionBar />}
                    <Grid>
                      <Grid item xs={12}>
                        {patient ? (
                          this.renderPatientHTML(patient, values, setFieldValue, hasValue)
                        ) : (
                          <></>
                        )}
                      </Grid>
                    </Grid>
                  </Form>
                )
              }}
            </Formik>
          </>
        ) : (
          <></>
        )}
        {deletePatientDialog}
      </>
    )
  }
}

export { Patient }

const InjectedPatient = inject<IStores, IPatientProps, Partial<IPatientProps>, any>(
  (stores: IStores) => ({
    close: stores.patients.closeDialog,
    closeTab: stores.global.closeTab,
    episodeOfCare: stores.patientEdit.selectedEpisodeOfCare,
    getPatientById: stores.patients.getPatientById,
    isConfirmDialogOpen: stores.patients.isConfirmDialogOpen,
    openConfirmDialog: stores.patients.openConfirmDialog,
    patient: stores.patients.selectedPatient,
    patientToQuotes: stores.patients.patientToQuotes,
    rerouteToPath: stores.global.rerouteToPath,
    resetPatient: stores.patients.resetPatient,
    savePatient: stores.patients.savePatient,
    setPageSize: stores.communications.setPageSize,
    setSelectedPatient: stores.patients.setSelectedPatient,
    setSelectedReferral: stores.referrals.setSelectedReferral,
    setReturn: stores.global.setReturn,
  })
)(observer(Patient))

const PatientWithRouter: React.FC = () => {
  const params = useParams<{ patientId?: string }>()
  const [isDirty, setIsDirty] = React.useState(false)
  const componentRef = React.useRef<Patient>(null)

  // Use shared unsaved changes hook
  const {
    showCancelDialog,
    handleCancelClick,
    handleCancelConfirm,
    handleCancelDismiss,
  } = useUnsavedChangesWarning(isDirty)

  const handleCancel = () => {
    if (componentRef.current) {
      ;(componentRef.current as any).returnToPatients()
    }
  }

  return (
    <>
      <InjectedPatient
        routeParams={params}
        ref={componentRef}
        showCancelDialog={showCancelDialog}
        handleCancelClick={handleCancelClick}
        handleCancelConfirm={(onNavigate) => {
          handleCancelConfirm(onNavigate)
        }}
        handleCancelDismiss={handleCancelDismiss}
        setIsDirty={setIsDirty}
      />
      <UnsavedChangesDialog
        open={showCancelDialog}
        onConfirm={() => handleCancelConfirm(handleCancel)}
        onDismiss={handleCancelDismiss}
      />
    </>
  )
}

export default PatientWithRouter
